import React from 'react';
import styled from 'styled-components';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import { CampaignBase } from '../../models';
import { CampaignChannel, WhatsAppTemplateCatalogItem } from '../../models';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { getAllowedCampaignPlaceholders } from './campaignBuilderUtils';

export interface CampaignPreviewPanelProps {
    campaign: CampaignBase;
    whatsappTemplate?: WhatsAppTemplateCatalogItem;
}

// ── Shared channel header ──────────────────────────────────────────────────────

const ChannelHeader = styled.div<{ $whatsapp?: boolean } & React.HTMLAttributes<HTMLDivElement>>`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${p => p.$whatsapp ? '#25d366' : 'var(--appBlue)'};
    margin-bottom: var(--spacer-XS);

    svg { font-size: 1rem !important; }
`;

// ── WhatsApp preview ───────────────────────────────────────────────────────────

const WhatsAppContainer = styled.div`
    position: relative;
    border-radius: var(--spacer-XS);
    padding: var(--spacer-M);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    min-height: 80px;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: url('/whatsapp-background-image.png') center / cover;
        opacity: 0.18;
        z-index: 0;
    }

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: #dfe5e0;
        z-index: -1;
    }

    > * {
        position: relative;
        z-index: 1;
    }
`;

const BubbleOuter = styled.div`
    position: relative;
    max-width: 82%;
`;

const BubbleTail = styled.div`
    position: absolute;
    top: 0;
    right: -7px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 0 10px 10px;
    border-color: transparent transparent transparent white;
    filter: drop-shadow(1px 0 1px rgba(0,0,0,0.08));
`;

const Bubble = styled.div`
    background: white;
    border-radius: 8px 0 8px 8px;
    padding: var(--spacer-S) var(--spacer-M);
    box-shadow: 0 1px 3px rgba(0,0,0,0.12);
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const BubbleHeader = styled.div`
    font-weight: 700;
    font-size: 0.92rem;
    color: #111;
    line-height: 1.3;
`;

const BubbleBody = styled.div`
    font-size: 0.88rem;
    color: #333;
    white-space: pre-wrap;
    line-height: 1.5;
`;

const BubbleFooter = styled.div`
    font-size: 0.75rem;
    color: #999;
    line-height: 1.3;
`;

const BubbleTime = styled.div`
    font-size: 0.68rem;
    color: #999;
    align-self: flex-end;
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 3px;
`;

const BubbleDivider = styled.div`
    height: 1px;
    background: rgba(0,0,0,0.08);
    margin: 2px calc(-1 * var(--spacer-M));
`;

const BubbleButtonRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const BubbleBtn = styled.div<React.HTMLAttributes<HTMLDivElement>>`
    text-align: center;
    color: #00a5f4;
    font-size: 0.82rem;
    font-weight: 500;
    padding: 4px 0;
`;

// ── Email preview ──────────────────────────────────────────────────────────────

const EmailMockContainer = styled.div`
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: var(--spacer-XS);
    overflow: hidden;
    background: white;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;

const EmailMeta = styled.div`
    padding: var(--spacer-S) var(--spacer-M);
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
    background: var(--bg-light);
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const EmailMetaRow = styled.div`
    display: flex;
    gap: var(--spacer-S);
    font-size: 0.82rem;
    color: var(--text-muted);
`;

const EmailMetaLabel = styled.span`
    font-weight: 600;
    min-width: 60px;
    flex-shrink: 0;
`;

const PlaceholderNote = styled.div`
    font-size: 0.78rem;
    color: var(--text-muted);
    margin-top: var(--spacer-XS);
`;

// ── SMS preview ────────────────────────────────────────────────────────────────

const SmsContainer = styled.div`
    background: var(--bg-light);
    border-radius: var(--spacer-XS);
    padding: var(--spacer-M);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    min-height: 60px;
`;

const SmsBubble = styled.div`
    background: var(--appBlue);
    color: white;
    border-radius: 16px 16px 4px 16px;
    padding: var(--spacer-S) var(--spacer-M);
    max-width: 82%;
    font-size: 0.88rem;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
`;

const SmsEmptyLabel = styled.div`
    font-size: 0.85rem;
    color: var(--text-muted);
    align-self: center;
    width: 100%;
    text-align: center;
    padding: var(--spacer-M) 0;
`;

// ── Shared ─────────────────────────────────────────────────────────────────────

const now = new Date();
const timeLabel = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

export const CampaignPreviewPanel: React.FC<CampaignPreviewPanelProps> = ({ campaign, whatsappTemplate }) => {
    const t = useTranslations();
    const isSms = campaign.channel === CampaignChannel.SMS;
    const isWhatsApp = campaign.channel === CampaignChannel.WhatsApp;

    if (isSms) {
        return (
            <div>
                <ChannelHeader>
                    <SmsIcon />
                    SMS
                </ChannelHeader>
                <SmsContainer>
                    {campaign.content ? (
                        <SmsBubble>{campaign.content}</SmsBubble>
                    ) : (
                        <SmsEmptyLabel>{t(TK.campaignPreviewEmpty)}</SmsEmptyLabel>
                    )}
                </SmsContainer>
                <PlaceholderNote>
                    <strong>{t(TK.campaignSupportedPlaceholders)}:</strong>{' '}
                    {'{client-name}'}, {'{company-name}'}
                </PlaceholderNote>
            </div>
        );
    }

    if (isWhatsApp) {
        const components = whatsappTemplate?.componentsSnapshot ?? [];
        const header = components.find(c => c.type?.toUpperCase() === 'HEADER');
        const body = components.find(c => c.type?.toUpperCase() === 'BODY');
        const footer = components.find(c => c.type?.toUpperCase() === 'FOOTER');
        const buttons = components.find(c => c.type?.toUpperCase() === 'BUTTONS')?.buttons ?? [];

        return (
            <div>
                <ChannelHeader $whatsapp>
                    <WhatsAppIcon />
                    WhatsApp
                </ChannelHeader>
                <WhatsAppContainer>
                    {whatsappTemplate ? (
                        <BubbleOuter>
                            <BubbleTail />
                            <Bubble>
                                {header?.text && <BubbleHeader>{header.text}</BubbleHeader>}
                                {body?.text && <BubbleBody>{body.text}</BubbleBody>}
                                {footer?.text && <BubbleFooter>{footer.text}</BubbleFooter>}
                                <BubbleTime>
                                    {timeLabel}
                                    <svg width="15" height="11" viewBox="0 0 15 11" fill="none">
                                        <path d="M1 5.5L4.5 9L14 1" stroke="#53bdeb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M5 5.5L8.5 9L14 3" stroke="#53bdeb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.7"/>
                                    </svg>
                                </BubbleTime>
                                {buttons.length > 0 && (
                                    <>
                                        <BubbleDivider />
                                        <BubbleButtonRow>
                                            {buttons.map((btn, i) => (
                                                <BubbleBtn key={i}>{btn.text}</BubbleBtn>
                                            ))}
                                        </BubbleButtonRow>
                                    </>
                                )}
                            </Bubble>
                        </BubbleOuter>
                    ) : (
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', alignSelf: 'center', width: '100%', textAlign: 'center', padding: 'var(--spacer-M) 0' }}>
                            {t(TK.notAvailable)}
                        </div>
                    )}
                </WhatsAppContainer>
            </div>
        );
    }

    // Email preview
    return (
        <div>
            <ChannelHeader>
                <EmailIcon />
                Email
            </ChannelHeader>
            <EmailMockContainer>
                <EmailMeta>
                    {campaign.subject && (
                        <EmailMetaRow>
                            <EmailMetaLabel>{t(TK.subject)}:</EmailMetaLabel>
                            <span>{campaign.subject}</span>
                        </EmailMetaRow>
                    )}
                    {campaign.senderName && (
                        <EmailMetaRow>
                            <EmailMetaLabel>{t(TK.fromName)}:</EmailMetaLabel>
                            <span>{campaign.senderName}</span>
                        </EmailMetaRow>
                    )}
                    {campaign.senderEmail && (
                        <EmailMetaRow>
                            <EmailMetaLabel>{t(TK.fromEmail)}:</EmailMetaLabel>
                            <span>{campaign.senderEmail}</span>
                        </EmailMetaRow>
                    )}
                    {campaign.preheader && (
                        <EmailMetaRow>
                            <EmailMetaLabel>{t(TK.preheader)}:</EmailMetaLabel>
                            <span>{campaign.preheader}</span>
                        </EmailMetaRow>
                    )}
                </EmailMeta>
                <iframe
                    title={t(TK.preview)}
                    style={{ width: '100%', minHeight: '280px', border: 0, background: 'white', display: 'block' }}
                    srcDoc={campaign.content || `<div style="padding:16px;color:#666">${t(TK.campaignPreviewEmpty)}</div>`}
                />
            </EmailMockContainer>
            <PlaceholderNote>
                <strong>{t(TK.campaignSupportedPlaceholders)}:</strong>{' '}
                {getAllowedCampaignPlaceholders().map(p => `{${p}}`).join(', ')}
            </PlaceholderNote>
        </div>
    );
};
